import clsx from 'clsx'
import type { Figure } from '@/types/dashboards'
import { chartSparklineBarStyle } from '@/config/chartTheme'
import FigureValue, { FigureDeltaBadge, figureToneClass } from './FigureValue'
import { PanelError, PanelSkeleton } from './Panel'

interface KpiTileProps {
  figure: Figure
  className?: string
}

/** Trailing-period trend, oldest first. A `null` period is a genuine gap
 * (short, dim tick) — never drawn as a zero-height bar. */
function Sparkline({ values }: { values: (number | null)[] }) {
  const numeric = values.filter((v): v is number => v != null)
  const max = numeric.length ? Math.max(...numeric, 0) : 0
  return (
    <div className="mt-1 flex h-4 items-end gap-[1.5px]" aria-hidden="true">
      {values.map((v, i) => {
        const heightPct = v == null || max <= 0 ? 0 : Math.max((v / max) * 100, 8)
        const style = chartSparklineBarStyle(i, values.length)
        return (
          <div
            key={i}
            className="animate-bar-rise min-w-0 flex-1 rounded-[1px]"
            style={
              v == null
                ? { height: 2, background: 'rgba(255,255,255,0.08)' }
                : { height: `${heightPct}%`, background: style.background, opacity: style.opacity }
            }
          />
        )
      })}
    </div>
  )
}

/** One headline number. Clicking the value opens its record set. */
export function KpiTile({ figure, className }: KpiTileProps) {
  return (
    <div className={clsx('metric-cell flex min-w-0 flex-col justify-between', className)}>
      <div className="metric-cell-label break-words leading-tight" title={figure.label}>
        {figure.label}
      </div>
      <div className={clsx('metric-cell-value truncate', figureToneClass(figure.tone))}>
        <FigureValue figure={figure} />
      </div>
      {figure.delta && (
        <div className="mt-0.5">
          <FigureDeltaBadge figure={figure} />
        </div>
      )}
      {figure.basis && (
        <div className="mt-0.5 text-[9px] leading-snug text-stone-500">
          {figure.basis}
        </div>
      )}
      {figure.trend && figure.trend.length > 1 && <Sparkline values={figure.trend} />}
    </div>
  )
}

interface KpiStripProps {
  tiles: Figure[]
  /** Tailwind grid template class, e.g. 'grid-cols-8'. */
  columnsClassName?: string
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void
  className?: string
}

export default function KpiStrip({
  tiles,
  columnsClassName = 'grid-cols-8',
  isLoading = false,
  error = null,
  onRetry,
  className,
}: KpiStripProps) {
  if (error) {
    return (
      <div className={className}>
        <PanelError error={error} onRetry={onRetry} />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={clsx('grid gap-2', columnsClassName, className)}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="metric-cell">
            <PanelSkeleton rows={2} />
          </div>
        ))}
      </div>
    )
  }

  if (tiles.length === 0) {
    return (
      <div
        className={clsx(
          'surface-panel rounded-md px-3 py-3 text-[11px] text-stone-500',
          className,
        )}
      >
        No headline figures for this partition yet.
      </div>
    )
  }

  return (
    <div className={clsx('grid gap-2', columnsClassName, className)}>
      {tiles.map((tile) => (
        <KpiTile key={tile.label} figure={tile} />
      ))}
    </div>
  )
}
